import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import type { Heat } from '../../../types/heats';

/** Statuts qu'on ne supprime pas : une heat en cours, ou finie avec ses résultats. */
const VERROUILLES = new Set(['preparing', 'countdown', 'running', 'finished', 'completed']);

const TOUS = 'all';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heats: Heat[];
  wodName: (heat: Heat) => string;
  categoryName: (heat: Heat) => string;
  loading: boolean;
  /** Événement passé : le verrou tombe, comme pour le statut en lot. */
  eventOver?: boolean;
  onConfirm: (heatIds: string[]) => void;
}

/**
 * Suppression d'une sélection de heats (2026-09-28).
 *
 * Remplace « Supprimer tout » : l'organisateur veut supprimer un jour, un WOD,
 * ou les heats restées vides d'un WOD éliminatoire. « Tout » reste une
 * sélection comme une autre (aucun filtre, tout cocher).
 *
 * Rien de pré-coché, et une seconde étape qui dit ce qui va partir avant
 * d'écrire : supprimer ne se défait pas. Les heats en cours ou terminées
 * restent visibles mais non cochables.
 */
const DeleteHeatsDialog: React.FC<Props> = ({
  open, onOpenChange, heats, wodName, categoryName, loading, eventOver = false, onConfirm,
}) => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [day, setDay] = React.useState(TOUS);
  const [wod, setWod] = React.useState(TOUS);
  const [category, setCategory] = React.useState(TOUS);
  const [emptyOnly, setEmptyOnly] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSelected([]);
      setDay(TOUS); setWod(TOUS); setCategory(TOUS);
      setEmptyOnly(false);
      setConfirming(false);
    }
  }, [open]);

  const estVerrouillee = React.useCallback(
    (h: Heat) => !eventOver && VERROUILLES.has((h.status || '').toLowerCase()),
    [eventOver],
  );

  const libelleJour = React.useCallback((date: string) => new Date(date).toLocaleDateString(
    i18n.language, { weekday: 'long', day: 'numeric', month: 'long' },
  ), [i18n.language]);

  // Options des filtres, tirées des heats elles-mêmes : proposer un WOD sans
  // heat donnerait une liste vide.
  const jours = React.useMemo(
    () => [...new Set(heats.map(h => h.date).filter((d): d is string => !!d))].sort(),
    [heats],
  );
  const wods = React.useMemo(() => {
    const m = new Map<string, string>();
    heats.forEach(h => { if (h.wod_id) m.set(h.wod_id, wodName(h)); });
    return [...m.entries()];
  }, [heats, wodName]);
  const categories = React.useMemo(() => {
    const m = new Map<string, string>();
    heats.forEach(h => m.set(categoryName(h), categoryName(h)));
    return [...m.keys()].sort();
  }, [heats, categoryName]);

  const visibles = React.useMemo(() => heats.filter(h =>
    (day === TOUS || h.date === day)
    && (wod === TOUS || h.wod_id === wod)
    && (category === TOUS || categoryName(h) === category)
    && (!emptyOnly || (h.participant_count || 0) === 0),
  ), [heats, day, wod, category, emptyOnly, categoryName]);

  const cochables = visibles.filter(h => !estVerrouillee(h));
  const toutCoche = cochables.length > 0 && cochables.every(h => selected.includes(h.id));

  const basculerTout = () => {
    const ids = cochables.map(h => h.id);
    setSelected(prev => toutCoche
      ? prev.filter(id => !ids.includes(id))
      : [...new Set([...prev, ...ids])]);
  };

  const choisies = heats.filter(h => selected.includes(h.id));
  const equipesPlacees = choisies.reduce((n, h) => n + (h.participant_count || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('heats.deleteHeats.title')}</DialogTitle>
        </DialogHeader>

        {confirming ? (
          <div className="space-y-2 text-sm">
            <p>{t('heats.deleteHeats.confirmHeats', { count: choisies.length })}</p>
            <p className={cn(equipesPlacees > 0 ? 'text-destructive' : 'text-muted-foreground')}>
              {equipesPlacees > 0
                ? t('heats.deleteHeats.confirmTeams', { count: equipesPlacees })
                : t('heats.deleteHeats.confirmNoTeams')}
            </p>
            <p className="text-muted-foreground">{t('heats.deleteHeats.irreversible')}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('heats.deleteHeats.description')}</p>
            {eventOver && <p className="text-sm text-muted-foreground">{t('heats.deleteHeats.eventOver')}</p>}

            <div className="flex flex-wrap items-center gap-2">
              {jours.length > 1 && (
                <Select value={day} onValueChange={setDay}>
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TOUS}>{t('heats.deleteHeats.allDays')}</SelectItem>
                    {jours.map(d => <SelectItem key={d} value={d}>{libelleJour(d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={wod} onValueChange={setWod}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TOUS}>{t('heats.filters.allWods')}</SelectItem>
                  {wods.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TOUS}>{t('heats.filters.allCategories')}</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={emptyOnly} onCheckedChange={(v) => setEmptyOnly(v === true)} />
                {t('heats.deleteHeats.emptyOnly')}
              </label>
            </div>

            <div className="max-h-[45vh] overflow-y-auto rounded border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={toutCoche}
                        disabled={cochables.length === 0}
                        onCheckedChange={basculerTout}
                        aria-label={t('heats.bulkStatus.selectAll')}
                      />
                    </TableHead>
                    <TableHead>{t('heats.table.heat')}</TableHead>
                    <TableHead>{t('heats.table.wod')}</TableHead>
                    <TableHead>{t('heats.table.category')}</TableHead>
                    <TableHead className="text-right">{t('heats.deleteHeats.teams')}</TableHead>
                    <TableHead>{t('heats.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                        {t('heats.deleteHeats.none')}
                      </TableCell>
                    </TableRow>
                  )}
                  {visibles.map(heat => {
                    const verrouille = estVerrouillee(heat);
                    return (
                      <TableRow
                        key={heat.id}
                        className={cn(verrouille && 'opacity-40')}
                        title={verrouille ? t('heats.deleteHeats.lockedHint') : undefined}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selected.includes(heat.id)}
                            disabled={verrouille}
                            onCheckedChange={() => setSelected(prev =>
                              prev.includes(heat.id)
                                ? prev.filter(id => id !== heat.id)
                                : [...prev, heat.id])}
                          />
                        </TableCell>
                        <TableCell className="font-semibold tabular-nums">
                          #{heat.heat_number}
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {jours.length > 1 && heat.date ? `${libelleJour(heat.date)} · ` : ''}
                            {heat.start_time?.slice(0, 5)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{wodName(heat)}</TableCell>
                        <TableCell className="text-sm">{categoryName(heat)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          {heat.participant_count || 0}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {t(`heats.statusLabels.${heat.status}`, { defaultValue: heat.status })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => (confirming ? setConfirming(false) : onOpenChange(false))}
          >
            {confirming ? t('heats.deleteHeats.back') : t('common.cancel')}
          </Button>
          {confirming ? (
            <Button variant="destructive" disabled={loading} onClick={() => onConfirm(selected)}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('heats.deleteHeats.confirm', { count: selected.length })}
            </Button>
          ) : (
            <Button
              variant="destructive"
              disabled={selected.length === 0}
              onClick={() => setConfirming(true)}
            >
              {t('heats.deleteHeats.next', { count: selected.length })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteHeatsDialog;
